<script setup lang="ts">
const productContent = useProductContentStore()
const products = useProductVariantsStore()
const route = useRoute<"subject">()

const { data: allLicences } = useLicences()

const folder = computed(() => {
  const matchingFolder = productContent.folders.find(p => {
    return p.id === productContent.activeSubject?.parentLocationId
  })
  return matchingFolder || null
})

const decodedFolderTitle = computed(() => {
  if (folder.value?.title) {
    return formatForURL(folder.value?.title)
  }
})

const subjectTitle = computed(
  () =>
    productContent.activeSubject?.shortTitle ||
    productContent.activeSubject?.title,
)

const showSubjectTitle = computed(
  () =>
    subjectTitle.value &&
    typeof productContent.activeSubject?.priority === "number",
)
</script>

<template>
  <u-feature-flag
    class="fixed top-0 left-0 z-50 p-4 text-gray-400 w-fit"
  >
    <UButtonNew to="/" variant="quite" size="sm" theme="white"
      >kunne.no</UButtonNew
    >
  </u-feature-flag>
  <div
    class="flex h-full w-full items-center justify-center bg-[--theme-darkest]"
  >
    <div
      class="flex flex-col items-center justify-center md:flex-row md:items-start md:gap-16 w-[90vw] md:w-[85vw] 2xl:w-[80vw]"
    >
      <u-side-nav
        :data="productContent.articleNav"
        :licence="!!allLicences?.length"
        :product="products.activeVariant"
        class="md:w-full"
      >
        <template #list-top>
          <li :key="0" class="mt-0 w-fit md:m-0 md:mt-1">
            <NuxtLink
              :to="
                folder?.title
                  ? `/produkter/${route.params.product}/${route.params.variant}#${decodedFolderTitle}`
                  : `/produkter/${route.params.product}/${route.params.variant}`
              "
              class="flex items-center"
            >
              <u-icon name="arrow-left" size="sm" class="mr-3.5" />
              <h4
                class="max-w-[200px] truncate font-extrabold lg:max-w-[300px]"
              >
                Tilbake {{ folder ? `til ${folder.title}` : "" }}
              </h4>
            </NuxtLink>
          </li>
          <li
            v-if="showSubjectTitle"
            class="items-start hidden w-full pb-3 mt-8 text-white justify-content-between md:flex md:border-b-2 md:border-white md:pb-2"
          >
            <div class="flex">
              <span
                class="subject-number menu-heading-large mr-3.5 flex w-[18px] items-center justify-center"
              >
                {{ productContent.activeSubject?.priority }}
              </span>
              <span class="menu-heading-large">
                {{ subjectTitle }}
              </span>
            </div>
            <UContentFreeLabel
              v-if="productContent.activeSubject?.showFreeLabel"
              class="ml-auto mr-2"
              :small="true"
            />
          </li>
        </template>
      </u-side-nav>
      <main
        class="h-full w-full max-w-[350px] lg:max-w-[45em] <md:flex <md:flex-col <md:items-center <md:justify-center"
      >
        <UMyProfileButton
          :path="[
            route.params.product,
            route.params.variant,
            route.params.subject,
          ]"
        />
        <div
          v-if="productContent.noSubjectsFound"
          class="richtext pb-32 text-white md:pt-[175px] 2xl:pt-[210px]"
        >
          <h1>Fant ikke kapittelet</h1>
          <NuxtLink
            class="block mt-6"
            :to="`/produkter/${route.params.product}/${route.params.variant}`"
          >
            Ta meg til forsiden av Kunne
            {{ products.activeProduct?.productTitle ?? "" }}
          </NuxtLink>
        </div>
        <template v-else>
          <div
            class="flex flex-col w-full mx-auto self-start gap-4 text-pretty pt-6"
          >
            <UContentFreeLabel
              v-if="productContent.activeSubject?.showFreeLabel"
              :small="true"
              class="md:hidden"
            />
            <h1
              v-if="
                showSubjectTitle &&
                !route.fullPath.endsWith('min-side')
              "
              class="text-white font-national2condensed text-mobile-article-h2 md:hidden"
            >
              {{
                `${productContent.activeSubject?.priority}. ${subjectTitle}`
              }}
            </h1>
          </div>
          <slot />
        </template>
      </main>
    </div>
  </div>
  <UBottomPanel />
</template>

<style scoped>
.menu-heading-large {
  @apply font-national2 text-[20px] font-bold not-italic !leading-6;
}
</style>
